import { omit } from 'ramda'
import { createSlice } from '@reduxjs/toolkit'
import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit'

const defaultFilterState = {
  filtersApplied: false,
  preset: null,
  appliedPreset: null,
  filters: null,
  lastUpdatedFilter: '',
  alcoholLevels: null,
  currencyDefault: '',
  priceInterval: null,
  confidenceInterval: null,
  filterErrors: [],
}

const initialState = {
  accBuyWineAnalysis: { ...defaultFilterState },
  accBuyWineOffers: { ...defaultFilterState },
  accBuyWineAnalysisRating: { ...defaultFilterState },
  accBuyWineWines: { ...defaultFilterState },
  accSellWineMyOffers: { ...defaultFilterState },
  accRetailEShops: { ...defaultFilterState },
  accRetailWines: { ...defaultFilterState },
  accRetailPriceReport: { ...defaultFilterState },
  accRetailMyRange: { ...defaultFilterState },
  accRetailBuyersWine: { ...defaultFilterState },
  accRetailGeoIndications: { ...defaultFilterState },
  accDatabaseProducers: { ...defaultFilterState },
  accDatabaseProducersDuplicates: { ...defaultFilterState },
  accDatabaseBrands: { ...defaultFilterState },
  accDatabaseWines: { ...defaultFilterState },
  accDatabaseAwards: { ...defaultFilterState },
  accDatabaseGeoIndication: { ...defaultFilterState },
  accDatabaseExhibitions: { ...defaultFilterState },
  accDatabaseKindOfWine: { ...defaultFilterState },
  accDatabaseSpecialClassificationCondition: { ...defaultFilterState },
  accReviewsMyReviews: { ...defaultFilterState },
  accAdministrationMerchants: { ...defaultFilterState },
  accAdministrationReviews: { ...defaultFilterState },
  accAdministrationOffers: { ...defaultFilterState },
  accAdministrationRequests: { ...defaultFilterState },
  accAdministrationUsers: { ...defaultFilterState },
  accAdministrationRequestsPotentialProducers: { ...defaultFilterState },
  accAdministrationCompanies: { ...defaultFilterState },
  accAdministrationRetailPublicPages: { ...defaultFilterState },
  accAdministrationELabels: { ...defaultFilterState },
  accAdministrationRetailOffers: { ...defaultFilterState },
  accAdministrationPriceSources: { ...defaultFilterState },
  accAdministrationWineSites: { ...defaultFilterState },
  accAdministrationMentions: { ...defaultFilterState },
  accAdministrationMentionsFacts: { ...defaultFilterState },
  accAdministrationMentionsPrices: { ...defaultFilterState },
  accAdministrationLinkMentions: { ...defaultFilterState },
  accAdministrationClusters: { ...defaultFilterState },
  accAdministrationVerifiedRelations: { ...defaultFilterState },
  accELabels: { ...defaultFilterState },
  accMyWinesWines: { ...defaultFilterState },
  accCapturedWines: { ...defaultFilterState },
}

// --------------- helpers ---------------

const setFiltersApplied = ({ state, table, payload }) => {
  state[table] = { ...state[table], filtersApplied: payload }
}
const setAlcoholLevels = ({ state, table, payload }) => {
  state[table] = { ...state[table], alcoholLevels: payload }
}
const setCurrencyDefault = ({ state, table, payload }) => {
  state[table] = { ...state[table], currencyDefault: payload }
}
const setPriceInterval = ({ state, table, payload }) => {
  state[table] = {
    ...state[table],
    priceInterval: {
      minValue: payload?.minValue,
      maxValue: Math.ceil(payload?.maxValue),
    },
  }
}
const setPriceIntervalByKey = ({ state, table, payload }) => {
  state[table] = {
    ...state[table],
    [payload.intervalKey]: {
      minValue: payload?.interval?.minValue,
      maxValue: Math.ceil(payload?.interval?.maxValue),
    },
  }
}
const setDrinkingWindowInterval = ({ state, table, payload }) => {
  state[table] = {
    ...state[table],
    drinkingWindow: {
      minValue: payload?.minValue,
      maxValue: Math.ceil(payload?.maxValue),
    },
  }
}
const setRrpInterval = ({ state, table, payload }) => {
  state[table] = {
    ...state[table],
    rrpInterval: {
      minValue: payload?.minValue,
      maxValue: Math.ceil(payload?.maxValue),
    },
  }
}
const setParticipantsInterval = ({ state, table, payload }) => {
  state[table] = {
    ...state[table],
    participantsFilter: {
      minValue: payload?.minValue,
      maxValue: Math.ceil(payload?.maxValue),
    },
  }
}
const setFilterByName = ({ state, table, payload }) => {
  state[table] = { ...state[table], [payload?.filterName]: payload?.filterData }
}
const setFilters = ({ state, table, payload }) => {
  state[table] = { ...state[table], filters: payload }
}
const removeFilter = ({ state, table, payload }) => {
  state[table] = {
    ...state[table],
    preset: null,
    filters: omit([payload], { ...state[table].filters }) || null,
  }
}
const setFilter = ({ state, table, payload }) => {
  state[table] = {
    ...state[table],
    preset: null,
    filters: { ...state[table].filters, ...payload },
  }
}
const setLastUpdatedFilter = ({ state, table, payload }) => {
  state[table] = { ...state[table], lastUpdatedFilter: payload }
}
const setAppliedPreset = ({ state, table, payload }) => {
  state[table] = { ...state[table], appliedPreset: payload }
}
const setPreset = ({ state, table, payload }) => {
  state[table] = { ...state[table], preset: payload }
}
const setFilterErrors = ({ state, table, payload }) => {
  let filterErrors = []
  if (payload?.action === 'add') {
    filterErrors = state[table].filterErrors.includes(payload.filterName)
      ? state[table].filterErrors
      : [...state[table].filterErrors, payload.filterName]
  } else if (payload?.action === 'remove') {
    filterErrors = state[table].filterErrors.filter(
      filter => filter !== payload.filterName
    )
  } else if (!payload?.action) {
    filterErrors = []
  }
  state[table] = { ...state[table], filterErrors }
}

// --------------- reducer factory ---------------

type State = typeof initialState
type TableKey = keyof State
type TableReducers = Record<string, CaseReducer<State, PayloadAction<any>>>

const makeBase = (prefix: string, table: TableKey) => ({
  [`${prefix}FiltersApplied`]: (state: State, { payload }) => {
    setFiltersApplied({ state, table, payload })
  },
  [`${prefix}Filters`]: (state: State, { payload }) => {
    setFilters({ state, table, payload })
  },
  [`${prefix}RemoveFilter`]: (state: State, { payload }) => {
    removeFilter({ state, table, payload })
  },
  [`${prefix}Filter`]: (state: State, { payload }) => {
    setFilter({ state, table, payload })
  },
  [`${prefix}LastUpdatedFilter`]: (state: State, { payload }) => {
    setLastUpdatedFilter({ state, table, payload })
  },
  [`${prefix}SetFilterErrors`]: (state: State, { payload }) => {
    setFilterErrors({ state, table, payload })
  },
  [`${prefix}Preset`]: (state: State, { payload }) => {
    setPreset({ state, table, payload })
  },
  [`${prefix}AppliedPreset`]: (state: State, { payload }) => {
    setAppliedPreset({ state, table, payload })
  },
})

const withAlcohol = (prefix: string, table: TableKey) => ({
  [`${prefix}AlcoholLevels`]: (state: State, { payload }) => {
    setAlcoholLevels({ state, table, payload })
  },
})
const withCurrency = (prefix: string, table: TableKey) => ({
  [`${prefix}CurrencyDefault`]: (state: State, { payload }) => {
    setCurrencyDefault({ state, table, payload })
  },
})
const withPrice = (prefix: string, table: TableKey) => ({
  [`${prefix}PriceInterval`]: (state: State, { payload }) => {
    setPriceInterval({ state, table, payload })
  },
})
const withPriceByKey = (prefix: string, table: TableKey) => ({
  [`${prefix}PriceInterval`]: (state: State, { payload }) => {
    setPriceIntervalByKey({ state, table, payload })
  },
})
const withByName = (actionKey: string, table: TableKey) => ({
  [actionKey]: (state: State, { payload }) => {
    setFilterByName({ state, table, payload })
  },
})
const withRrp = (prefix: string, table: TableKey) => ({
  [`${prefix}Rrp`]: (state: State, { payload }) => {
    setRrpInterval({ state, table, payload })
  },
})
const withParticipants = (prefix: string, table: TableKey) => ({
  [`${prefix}Participants`]: (state: State, { payload }) => {
    setParticipantsInterval({ state, table, payload })
  },
})
const withDrinkingWindow = (prefix: string, table: TableKey) => ({
  [`${prefix}DrinkingWindow`]: (state: State, { payload }) => {
    setDrinkingWindowInterval({ state, table, payload })
  },
})

// --------------- table reducers ---------------

const buyWineAnalysisReducers = {
  ...makeBase('setAccBuyWineAnalysis', 'accBuyWineAnalysis'),
  ...withAlcohol('setAccBuyWineAnalysis', 'accBuyWineAnalysis'),
  ...withCurrency('setAccBuyWineAnalysis', 'accBuyWineAnalysis'),
  ...withPrice('setAccBuyWineAnalysis', 'accBuyWineAnalysis'),
  ...withRrp('setAccBuyWineAnalysis', 'accBuyWineAnalysis'),
  ...withByName('setAccBuyWineAnalysisByName', 'accBuyWineAnalysis'),
}

const buyWineOffersReducers = {
  ...makeBase('setAccBuyWineOffers', 'accBuyWineOffers'),
  ...withAlcohol('setAccBuyWineOffers', 'accBuyWineOffers'),
  ...withCurrency('setAccBuyWineOffers', 'accBuyWineOffers'),
  ...withPrice('setAccBuyWineOffers', 'accBuyWineOffers'),
  ...withByName('setAccBuyWineOffersByName', 'accBuyWineOffers'),
}

const buyWineAnalysisRatingReducers = {
  ...makeBase('setAccBuyWineAnalysisRating', 'accBuyWineAnalysisRating'),
  ...withAlcohol('setAccBuyWineAnalysisRating', 'accBuyWineAnalysisRating'),
  ...withCurrency('setAccBuyWineAnalysisRating', 'accBuyWineAnalysisRating'),
  ...withPrice('setAccBuyWineAnalysisRating', 'accBuyWineAnalysisRating'),
  ...withByName(
    'setAccBuyWineAnalysisRatingByName',
    'accBuyWineAnalysisRating'
  ),
}

const buyWineWinesReducers = {
  ...makeBase('setAccBuyWineWines', 'accBuyWineWines'),
  ...withAlcohol('setAccBuyWineWines', 'accBuyWineWines'),
  ...withCurrency('setAccBuyWineWines', 'accBuyWineWines'),
  ...withPrice('setAccBuyWineWines', 'accBuyWineWines'),
  ...withByName('setAccBuyWineWinesByName', 'accBuyWineWines'),
}

const sellWineMyOffersReducers = {
  ...makeBase('setAccSellWineMyOffers', 'accSellWineMyOffers'),
  ...withAlcohol('setAccSellWineMyOffers', 'accSellWineMyOffers'),
  ...withCurrency('setAccSellWineMyOffers', 'accSellWineMyOffers'),
  ...withPrice('setAccSellWineMyOffers', 'accSellWineMyOffers'),
}

const retailEShopsReducers = {
  ...makeBase('setAccRetailEShops', 'accRetailEShops'),
  ...withAlcohol('setAccRetailEShops', 'accRetailEShops'),
  ...withCurrency('setAccRetailEShops', 'accRetailEShops'),
  ...withPrice('setAccRetailEShops', 'accRetailEShops'),
  ...withByName('setAccRetailEShopsByName', 'accRetailEShops'),
}

const retailWinesReducers = {
  ...makeBase('setAccRetailWines', 'accRetailWines'),
  ...withAlcohol('setAccRetailWines', 'accRetailWines'),
  ...withCurrency('setAccRetailWines', 'accRetailWines'),
  ...withPrice('setAccRetailWines', 'accRetailWines'),
  ...withByName('setAccRetailWinesByName', 'accRetailWines'),
}

const retailPriceReportReducers = {
  ...makeBase('setAccRetailPriceReport', 'accRetailPriceReport'),
  ...withAlcohol('setAccRetailPriceReport', 'accRetailPriceReport'),
  ...withCurrency('setAccRetailPriceReport', 'accRetailPriceReport'),
  ...withPrice('setAccRetailPriceReport', 'accRetailPriceReport'),
  ...withByName('setAccRetailPriceReportByName', 'accRetailPriceReport'),
}

const retailMyRangeReducers = {
  ...makeBase('setAccRetailMyRange', 'accRetailMyRange'),
  ...withAlcohol('setAccRetailMyRange', 'accRetailMyRange'),
  ...withCurrency('setAccRetailMyRange', 'accRetailMyRange'),
  ...withPrice('setAccRetailMyRange', 'accRetailMyRange'),
  ...withByName('setAccRetailMyRangeByName', 'accRetailMyRange'),
}

const retailBuyersWineReducers = {
  ...makeBase('setAccRetailBuyersWine', 'accRetailBuyersWine'),
  ...withAlcohol('setAccRetailBuyersWine', 'accRetailBuyersWine'),
  ...withCurrency('setAccRetailBuyersWine', 'accRetailBuyersWine'),
  ...withPrice('setAccRetailBuyersWine', 'accRetailBuyersWine'),
  ...withByName('setAccRetailBuyersWineByName', 'accRetailBuyersWine'),
}

const retailGeoIndicationsReducers = {
  ...makeBase('setAccRetailGeoIndications', 'accRetailGeoIndications'),
  ...withAlcohol('setAccRetailGeoIndications', 'accRetailGeoIndications'),
  ...withCurrency('setAccRetailGeoIndications', 'accRetailGeoIndications'),
  ...withPrice('setAccRetailGeoIndications', 'accRetailGeoIndications'),
  ...withByName('setAccRetailGeoIndicationsByName', 'accRetailGeoIndications'),
}

const databaseProducersReducers = {
  ...makeBase('setAccDatabaseProducers', 'accDatabaseProducers'),
  ...withAlcohol('setAccDatabaseProducers', 'accDatabaseProducers'),
  ...withCurrency('setAccDatabaseProducers', 'accDatabaseProducers'),
  ...withPriceByKey('setAccDatabaseProducers', 'accDatabaseProducers'),
}

const databaseProducersDuplicatesReducers = {
  ...makeBase(
    'setAccDatabaseProducersDuplicates',
    'accDatabaseProducersDuplicates'
  ),
  ...withAlcohol(
    'setAccDatabaseProducersDuplicates',
    'accDatabaseProducersDuplicates'
  ),
  ...withCurrency(
    'setAccDatabaseProducersDuplicates',
    'accDatabaseProducersDuplicates'
  ),
  ...withPrice(
    'setAccDatabaseProducersDuplicates',
    'accDatabaseProducersDuplicates'
  ),
}

const databaseBrandsReducers = {
  ...makeBase('setAccDatabaseBrands', 'accDatabaseBrands'),
}

const databaseWinesReducers = {
  ...makeBase('setAccDatabaseWines', 'accDatabaseWines'),
  ...withAlcohol('setAccDatabaseWines', 'accDatabaseWines'),
  ...withCurrency('setAccDatabaseWines', 'accDatabaseWines'),
  ...withPriceByKey('setAccDatabaseWines', 'accDatabaseWines'),
}

// minimal: only Filters/RemoveFilter/Filter
const databaseAwardsReducers = {
  setAccDatabaseAwardsFilters: (state: State, { payload }) => {
    setFilters({ state, table: 'accDatabaseAwards', payload })
  },
  setAccDatabaseAwardsRemoveFilter: (state: State, { payload }) => {
    removeFilter({ state, table: 'accDatabaseAwards', payload })
  },
  setAccDatabaseAwardsFilter: (state: State, { payload }) => {
    setFilter({ state, table: 'accDatabaseAwards', payload })
  },
}

const databaseGeoIndicationReducers = {
  ...makeBase('setAccDatabaseGeoIndication', 'accDatabaseGeoIndication'),
}

const databaseExhibitionsReducers = {
  ...makeBase('setAccDatabaseExhibitions', 'accDatabaseExhibitions'),
  ...withParticipants('setAccDatabaseExhibitions', 'accDatabaseExhibitions'),
}

const databaseKindOfWineReducers = {
  ...makeBase('setAccDatabaseKindOfWine', 'accDatabaseKindOfWine'),
}

const databaseSpecClasConditionReducers = {
  ...makeBase(
    'setAccDatabaseSpecialClassificationCondition',
    'accDatabaseSpecialClassificationCondition'
  ),
}

const myReviewsReducers = {
  ...makeBase('setAccReviewsMyReviews', 'accReviewsMyReviews'),
  ...withAlcohol('setAccReviewsMyReviews', 'accReviewsMyReviews'),
}

const adminOffersReducers = {
  ...makeBase('setAccAdministrationOffers', 'accAdministrationOffers'),
  ...withAlcohol('setAccAdministrationOffers', 'accAdministrationOffers'),
  ...withCurrency('setAccAdministrationOffers', 'accAdministrationOffers'),
  ...withPrice('setAccAdministrationOffers', 'accAdministrationOffers'),
  ...withByName('setAccAdministrationOffersByName', 'accAdministrationOffers'),
}

const adminRequestsReducers = {
  ...makeBase('setAccAdministrationRequests', 'accAdministrationRequests'),
  ...withAlcohol('setAccAdministrationRequests', 'accAdministrationRequests'),
  ...withCurrency('setAccAdministrationRequests', 'accAdministrationRequests'),
  ...withPrice('setAccAdministrationRequests', 'accAdministrationRequests'),
}

const adminUsersReducers = {
  ...makeBase('setAccAdministrationUsers', 'accAdministrationUsers'),
}

const adminPotentialProducersReducers = {
  ...makeBase(
    'setAccAdministrationRequestsPotentialProducers',
    'accAdministrationRequestsPotentialProducers'
  ),
}

const adminCompaniesReducers = {
  ...makeBase('setAccAdministrationCompanies', 'accAdministrationCompanies'),
}

const adminRetailPublicPagesReducers = {
  ...makeBase(
    'setAccAdministrationRetailPublicPages',
    'accAdministrationRetailPublicPages'
  ),
  ...withAlcohol(
    'setAccAdministrationRetailPublicPages',
    'accAdministrationRetailPublicPages'
  ),
  ...withCurrency(
    'setAccAdministrationRetailPublicPages',
    'accAdministrationRetailPublicPages'
  ),
  ...withPrice(
    'setAccAdministrationRetailPublicPages',
    'accAdministrationRetailPublicPages'
  ),
  ...withByName(
    'setAccAdministrationRetailPublicPagesByName',
    'accAdministrationRetailPublicPages'
  ),
}

const adminELabelsReducers = {
  ...makeBase('setAccAdministrationELabels', 'accAdministrationELabels'),
}

const adminTradeOffersReducers = {
  ...makeBase(
    'setAccAdministrationRetailOffers',
    'accAdministrationRetailOffers'
  ),
  ...withAlcohol(
    'setAccAdministrationRetailOffers',
    'accAdministrationRetailOffers'
  ),
  ...withCurrency(
    'setAccAdministrationRetailOffers',
    'accAdministrationRetailOffers'
  ),
  ...withPrice(
    'setAccAdministrationRetailOffers',
    'accAdministrationRetailOffers'
  ),
  ...withByName(
    'setAccAdministrationRetailOffersByName',
    'accAdministrationRetailOffers'
  ),
}

const adminPriceSourcesReducers = {
  ...makeBase(
    'setAccAdministrationPriceSources',
    'accAdministrationPriceSources'
  ),
}

const adminWineSitesReducers = {
  ...makeBase('setAccAdministrationWineSites', 'accAdministrationWineSites'),
  ...withAlcohol('setAccAdministrationWineSites', 'accAdministrationWineSites'),
  ...withByName(
    'setAccAdministrationWineSitesByName',
    'accAdministrationWineSites'
  ),
}

const adminMentionsReducers = {
  ...makeBase('setAccAdministrationMentions', 'accAdministrationMentions'),
  ...withAlcohol('setAccAdministrationMentions', 'accAdministrationMentions'),
  ...withByName(
    'setAccAdministrationMentionsByName',
    'accAdministrationMentions'
  ),
}
const adminMentionsFactsReducers = {
  ...makeBase(
    'setAccAdministrationMentionsFacts',
    'accAdministrationMentionsFacts'
  ),
  ...withAlcohol(
    'setAccAdministrationMentionsFacts',
    'accAdministrationMentionsFacts'
  ),
  ...withByName(
    'setAccAdministrationMentionsFactsByName',
    'accAdministrationMentionsFacts'
  ),
}

const adminMentionsPricesReducers = {
  ...makeBase(
    'setAccAdministrationMentionsPrices',
    'accAdministrationMentionsPrices'
  ),
  ...withAlcohol(
    'setAccAdministrationMentionsPrices',
    'accAdministrationMentionsPrices'
  ),
  ...withPrice(
    'setAccAdministrationMentionsPrices',
    'accAdministrationMentionsPrices'
  ),
  ...withByName(
    'setAccAdministrationMentionsPricesByName',
    'accAdministrationMentionsPrices'
  ),
}

const adminLinkMentionsReducers = {
  ...makeBase(
    'setAccAdministrationLinkMentions',
    'accAdministrationLinkMentions'
  ),
  ...withAlcohol(
    'setAccAdministrationLinkMentions',
    'accAdministrationLinkMentions'
  ),
  ...withByName(
    'setAccAdministrationLinkMentionsByName',
    'accAdministrationLinkMentions'
  ),
}

const adminClustersReducers = {
  ...makeBase('setAccAdministrationClusters', 'accAdministrationClusters'),
  ...withAlcohol('setAccAdministrationClusters', 'accAdministrationClusters'),
}

const adminVerifiedRelationsReducers = {
  ...makeBase(
    'setAccAdministrationVerifiedRelations',
    'accAdministrationVerifiedRelations'
  ),
  ...withAlcohol(
    'setAccAdministrationVerifiedRelations',
    'accAdministrationVerifiedRelations'
  ),
  ...withByName(
    'setAccAdministrationVerifiedRelationsByName',
    'accAdministrationVerifiedRelations'
  ),
}

const adminReviewsReducers = {
  ...makeBase('setAccAdministrationReviews', 'accAdministrationReviews'),
  ...withAlcohol('setAccAdministrationReviews', 'accAdministrationReviews'),
  ...withDrinkingWindow(
    'setAccAdministrationReviews',
    'accAdministrationReviews'
  ),
}

const eLabelsReducers = {
  ...makeBase('setAccELabels', 'accELabels'),
  ...withAlcohol('setAccELabels', 'accELabels'),
}

const myWinesReducers = {
  ...makeBase('setAccMyWinesWines', 'accMyWinesWines'),
  ...withAlcohol('setAccMyWinesWines', 'accMyWinesWines'),
  ...withByName('setAccMyWinesWinesByName', 'accMyWinesWines'),
}

const capturedWinesReducers = {
  ...makeBase('setAccCapturedWines', 'accCapturedWines'),
  ...withAlcohol('setAccCapturedWines', 'accCapturedWines'),
  ...withByName('setAccCapturedWinesByName', 'accCapturedWines'),
}

// --------------- slice ---------------

const reducers = {
  clear: () => initialState,
  setPerPage: (state: State, { payload }) => ({ ...state, perPage: payload }),
  ...buyWineAnalysisReducers,
  ...buyWineOffersReducers,
  ...buyWineAnalysisRatingReducers,
  ...buyWineWinesReducers,
  ...sellWineMyOffersReducers,
  ...retailEShopsReducers,
  ...retailWinesReducers,
  ...retailPriceReportReducers,
  ...retailMyRangeReducers,
  ...retailBuyersWineReducers,
  ...retailGeoIndicationsReducers,
  ...databaseProducersReducers,
  ...databaseProducersDuplicatesReducers,
  ...databaseBrandsReducers,
  ...databaseWinesReducers,
  ...databaseAwardsReducers,
  ...databaseGeoIndicationReducers,
  ...databaseExhibitionsReducers,
  ...databaseKindOfWineReducers,
  ...databaseSpecClasConditionReducers,
  ...myReviewsReducers,
  ...adminOffersReducers,
  ...adminRequestsReducers,
  ...adminUsersReducers,
  ...adminPotentialProducersReducers,
  ...adminCompaniesReducers,
  ...adminRetailPublicPagesReducers,
  ...adminELabelsReducers,
  ...adminTradeOffersReducers,
  ...adminPriceSourcesReducers,
  ...adminWineSitesReducers,
  ...adminMentionsReducers,
  ...adminMentionsFactsReducers,
  ...adminMentionsPricesReducers,
  ...adminLinkMentionsReducers,
  ...adminClustersReducers,
  ...adminVerifiedRelationsReducers,
  ...adminReviewsReducers,
  ...eLabelsReducers,
  ...myWinesReducers,
  ...capturedWinesReducers,
} satisfies TableReducers

const tableFiltersSlice = createSlice({
  name: 'tableFilters',
  initialState,
  reducers,
})

export const { actions, reducer: tableFilters } = tableFiltersSlice
